import { useCallback, useEffect, useRef, useState } from "react";
import { loadProductHuntCSV } from "../components/data/productHuntDataLoader";

const emptyMetadata = { rowsCount: 0, columns: [] };

const initialState = {
  status: "idle",
  rows: [],
  metadata: emptyMetadata,
  error: null,
};

export function useProductHuntDataset(datasetPath) {
  const [state, setState] = useState(initialState);
  const controllerRef = useRef(null);

  const computeMetadata = useCallback((rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return emptyMetadata;
    const columnSet = new Set();
    let displayableCount = 0;
    rows.forEach((row) => {
      if (!row) return;
      Object.keys(row).forEach((key) => columnSet.add(key));
      const title = row.title ? String(row.title).trim() : "";
      if (title) {
        displayableCount += 1;
      }
    });
    return {
      rowsCount: displayableCount,
      columns: Array.from(columnSet),
    };
  }, []);

  const fetchRows = useCallback(async () => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setState((prev) => ({ ...prev, status: "loading", error: null }));

    try {
      const { rows } = await loadProductHuntCSV(datasetPath, {
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      setState({
        status: "success",
        rows,
        metadata: computeMetadata(rows),
        error: null,
      });
    } catch (err) {
      if (controller.signal.aborted) return;
      setState((prev) => ({
        ...prev,
        status: "error",
        error: err.message || "Failed to load data",
      }));
    }
  }, [datasetPath, computeMetadata]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const refetch = useCallback(() => {
    fetchRows();
  }, [fetchRows]);

  return { ...state, refetch };
}
